import {
  Component,
  Output,
  EventEmitter,
  AfterViewInit,
  ChangeDetectionStrategy,
  input,
  inject,
  computed,
  linkedSignal,
  signal,
  effect,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { ClrDatagridFilter, ClarityModule, ClrDatagridFilterInterface, ClrPopoverPosition } from '@clr/angular';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { FilterFactory } from 'src/app/shared/filter/filterfactory';
import { DataType, emptyValueSign, isBooleanType } from 'src/app/models/ui/datatype';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import {
  OperatorFactory,
  betweenOperator,
  isEmptyOperator,
  isNotEmptyOperator,
} from 'src/app/shared/filter/operatorfactory';
import { Column } from 'src/app/models/ui/table';
import { FilterItem, FilterOperator } from 'src/app/models/ui/filter';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { IsRep } from 'src/app/services/_offline/dexieDB';
import { FkColorsState } from 'src/app/services/fk-colors.state';
import { intersectionBy } from 'src/app/misc/functions';
import { TextBoolDgCell } from '../../_shared/datagrid-cell/text-bool-dg-cell';

//#region Interfaces
export enum ColumnFilterAction {
  Reset,
  Submit,
  Redirect,
  ResetAll,
}

export interface ColumnFilterApplied {
  reason: ColumnFilterAction.Submit | ColumnFilterAction.Redirect;
  columnId: string;
  items: FilterItem[];
}

export type ColumnFilterChange =
  | ColumnFilterApplied
  | {
      reason: ColumnFilterAction.ResetAll;
    }
  | {
      reason: ColumnFilterAction.Reset;
      columnId: string;
    };

const ICON_COLUMNS = ['typ', 'rep', 'sp', 'sperrstatus', 'lf', 'lfstatus', 'st', 'si', 'abnahmestatus', 'minfk'];
//#endregion

@Component({
  selector: 'app-column-filter',
  templateUrl: './column-filter.component.html',
  styleUrls: ['./column-filter.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    ClarityModule,
    AutofocusDirective,
    GetByCallbackPipe,
    MrTranslatePipe,
    TextBoolDgCell,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ColumnFilterComponent implements AfterViewInit, ClrDatagridFilterInterface<any> {
  private readonly filterContainer = inject(ClrDatagridFilter);
  private readonly fkColorsState = inject(FkColorsState);

  readonly column = input<Column>({} as Column);
  readonly uniqueColumnValues = input<Set<any>>(new Set());

  readonly title = input<string>(undefined);
  readonly objektTypIconValue = input<Map<string, number>>(undefined);

  protected readonly dataType = computed(() => (this.column().type?.toLowerCase() ?? 'string') as DataType);
  protected readonly isBoolean = computed(() => isBooleanType(this.dataType()));
  protected readonly operators = computed(() => new OperatorFactory().getOperators(this.dataType()));

  protected readonly operator = linkedSignal<FilterOperator>(() => this.operators()[0]);
  protected readonly isOperatorVisible = computed(() => !this.isBoolean());
  protected readonly isInputVisible = computed(
    () => !this.isBoolean() && this.operator() !== isEmptyOperator && this.operator() !== isNotEmptyOperator
  );
  protected readonly inputType = computed(() =>
    this.dataType() == 'string' ? 'text'
    : this.dataType() == 'datetime' ? 'date'
    : 'number'
  );
  protected readonly id = computed(() => this.column().id.toLowerCase());

  protected readonly fkColors = computed(() => (this.id() == 'minfk' ? this.fkColorsState.fkColorsMap() : {}));

  protected readonly isIconColumn = computed(() => this.isBoolean() || ICON_COLUMNS.includes(this.id()));
  protected readonly iconSize = computed(() =>
    this.isBoolean() || !this.isIconColumn() ? '16'
    : this.id() == 'abnahmestatus' ? '22'
    : '32'
  );

  protected readonly enableSecondSearchInput = computed(() => this.operator() === betweenOperator);
  protected readonly modalOpened = signal(false);
  protected readonly selectedAll = signal(true);
  protected readonly isSubmitted = signal(false);
  protected readonly isResetted = signal(false);

  protected readonly input1 = signal('');
  protected readonly input2 = signal('');
  private readonly filter = computed(() => new FilterFactory().createFilter(this.originalItems(), this.dataType()));
  private readonly originalItems = computed(() => {
    try {
      if (!this.uniqueColumnValues()) return [];
      let hasEmptyValue = false;
      const mappedValues: FilterItem[] = [];
      const sortedValues = [...this.uniqueColumnValues()].sort((a, b) => {
        switch (typeof a) {
          case 'number':
            return a - b;
          case 'string':
            return a.localeCompare(b);
        }
      });
      for (const value of sortedValues ?? []) {
        if (value || value === 0) mappedValues.push({ name: value });
        else if (!hasEmptyValue) {
          mappedValues.unshift({ name: emptyValueSign, isEmptyValue: true });
          hasEmptyValue = true;
        }
      }
      return mappedValues;
    } catch (exception) {
      console.error(exception);
    }
  });

  protected readonly filteredItems = linkedSignal<FilterItem[]>(() => {
    try {
      const filter = this.filter();
      const input1 = this.input1();
      const input2 = this.input2();
      const operator = this.operator();

      const isInputIndependentOperator = !operator || operator === isNotEmptyOperator || operator === isEmptyOperator;

      if (isInputIndependentOperator || input1) return filter.filter(input1, input2, operator) ?? [];
      else return this.originalItems() ?? [];
    } catch (error) {
      console.error(error);
    }
  });
  protected readonly selectedItems = linkedSignal<FilterItem[]>(() => this.filteredItems());
  protected readonly isAllSelected = computed(() => this.selectedItems().length == this.originalItems().length);

  // Store the last submitted state
  private lastSubmittedOperator: FilterOperator;
  private lastSubmittedInput1: string = '';
  private lastSubmittedInput2: string = '';
  private lastSubmittedSelectedItems: FilterItem[] = [];
  private lastSubmittedFilteredItems: FilterItem[] = [];
  //#region interface needs
  readonly isActive = computed(() => !this.isAllSelected() && this.isSubmitted());
  @Output() readonly changes = new EventEmitter<ColumnFilterChange>();

  state?: any;
  accepts(_: any): boolean {
    return true;
  }
  equals?(_: ClrDatagridFilterInterface<any, any>): boolean {
    return true;
  }
  filterValue: any;
  //#endregion

  constructor() {
    // * register in Clarity to get auto icon in AktivState
    this.filterContainer.customFilter = this;

    this.filterContainer.popoverPosition = ClrPopoverPosition.BOTTOM_MIDDLE;
    this.filterContainer.openChange.pipe(takeUntilDestroyed()).subscribe(this.modalOpened.set);

    const initOnceEffect = effect(() => {
      if (this.originalItems().length)
        untracked(() => {
          this.isResetted.set(true);
          this.saveLastSubmittedState();
          initOnceEffect.destroy();
        });
    });

    effect(() => this.selectedItems.set(this.selectedAll() ? this.filteredItems() : []));
  }

  ngAfterViewInit() {
    if (!IsRep() && !this.column().type)
      console.error(
        `The column ${this.column().id} - ${this.column().bezeichnung} doesn't 
      seem to have a proper data type: ${this.column().type}.
      Please inform the administrator if you see this as an issue.`
      );
  }

  protected resetWithEmit() {
    this.resetItems();
    this.changes.emit({ reason: ColumnFilterAction.Reset, columnId: this.column().id });
  }

  protected closeSubmit() {
    if (this.isAllSelected()) {
      this.resetWithEmit();
      return;
    }
    this.saveLastSubmittedState();
    this.changes.emit({
      reason: ColumnFilterAction.Submit,
      columnId: this.column().id,
      items: this.selectedItems(),
    });
    this.isSubmitted.set(true);
    this.isResetted.set(false);
    this.filterContainer.open = false;
  }

  protected closeCancel() {
    this.restoreLastSubmittedState();
    this.filterContainer.open = false;
  }

  public restoreFilter(restore: ColumnFilterChange) {
    if (restore.reason == ColumnFilterAction.Submit && restore.items.length) {
      const valid = intersectionBy(restore.items, this.originalItems(), 'name');
      if (!valid.length) {
        this.resetWithEmit();
        return;
      }
      this.filteredItems.set(valid);
      this.isResetted.set(false);
      this.isSubmitted.set(true);
      this.saveLastSubmittedState();
    }
  }

  public resetItems() {
    this.isSubmitted.set(false);
    this.isResetted.set(true);
    this.operator.set(this.operators()[0]);
    this.input1.set('');
    this.input2.set('');
    this.filteredItems.set([...this.originalItems()]);
    this.saveLastSubmittedState();
  }

  private saveLastSubmittedState() {
    this.lastSubmittedOperator = this.operator();
    this.lastSubmittedInput1 = this.input1();
    this.lastSubmittedInput2 = this.input2();
    this.lastSubmittedSelectedItems = [...this.selectedItems()];
    this.lastSubmittedFilteredItems = [...this.filteredItems()];
  }

  private restoreLastSubmittedState() {
    this.operator.set(this.lastSubmittedOperator);
    this.input1.set(this.lastSubmittedInput1);
    this.input2.set(this.lastSubmittedInput2);
    this.filteredItems.set([...this.lastSubmittedFilteredItems]);
    this.selectedItems.set([...this.lastSubmittedSelectedItems]);
  }

  protected readonly iconShape = (value: unknown): string => {
    if (this.isBoolean()) {
      if (isTruthy(value)) return 'success-standard';
      if (isFalsy(value)) return 'circle';
      return 'help';
    }

    switch (this.id()) {
      case 'typ':
        const id = this.objektTypIconValue().get(value as string);
        return value && id ? 'objekttyp' + id : 'unknown-status';
      case 'rep':
        return value !== '-' ? 'shield-x' : 'shield-check';
      case 'sp':
      case 'sperrstatus':
        return value && value !== '0' ? 'richtung' + value : 'blank';
      case 'lf':
      case 'lfstatus': {
        const num = +value;
        return !Number.isFinite(num) || num == 0 ? 'blank' : `lastelle${num / 10}`.replace('.', '_');
      }
      case 'st':
        return value === 1 ? 'exclamation-triangle' : 'blank';
      case 'si':
        return value === 1 ? 'warning-standard' : 'blank';
      case 'abnahmestatus':
        return value === 1 ? 'certificate' : 'blank';
      case 'minfk':
        return 'circle';
      default:
        return 'blank';
    }
  };

  protected readonly iconColor = (value: unknown): string | null => {
    if (this.isBoolean()) {
      return isTruthy(value) ? '#2EC0FF' : null;
    }
    switch (this.id()) {
      case 'rep':
        return value !== '-' ? 'red' : 'green';
      case 'si':
        return 'red';
      case 'typ':
      case 'lf':
      case 'lfstatus':
      case 'sp':
      case 'sperrstatus':
        return 'black';
      case 'minfk':
        return this.fkColorsState.fromFK(+value);
      default:
        return null;
    }
  };

  protected readonly iconIsSolid = (value: unknown): boolean =>
    (this.isBoolean() && isTruthy(value)) || this.id() == 'minfk';
}

function isTruthy(value: unknown) {
  return value === true || value === 'true' || value === '1';
}
function isFalsy(value: unknown) {
  return value === false || value === 'false' || value === '0' || value === '-';
}
